import type { CanvasEntity } from '../entityTypes';
import type { CanvasGroup, CanvasLayer } from '../types';
import type { GlobalTasksById } from '../../stores/types';
import { getHandler } from '../entities/registry';
import { buildGroupBoundsMap } from './buildGroupBoundsMap';
import { resolveGroupRedirect } from './resolveGroupRedirect';

function rectsIntersect(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function pointInRect(
  px: number,
  py: number,
  rect: { x: number; y: number; width: number; height: number },
): boolean {
  return px >= rect.x && px <= rect.x + rect.width && py >= rect.y && py <= rect.y + rect.height;
}

export interface BoxSelectInput {
  rect: { x: number; y: number; width: number; height: number };
  rawEntities: CanvasEntity[];
  layers: CanvasLayer[];
  groups: CanvasGroup[];
  tasksById: GlobalTasksById;
  enteredGroupId: string | null;
}

export function resolveBoxSelectMatches(input: BoxSelectInput): string[] {
  const { rect, rawEntities, layers, groups, tasksById, enteredGroupId } = input;

  // Pre-compute hidden & locked layer sets
  const hiddenLayerIds = new Set<string>();
  const lockedLayerIds = new Set<string>();
  for (const layer of layers) {
    if (!layer.visible) hiddenLayerIds.add(layer.id);
    if (layer.locked) lockedLayerIds.add(layer.id);
  }

  // Filter to valid non-connector, non-group entities
  const entities = rawEntities.filter(e => {
    if (!getHandler(e.kind).hasOwnRect) return false;
    if (e.kind === 'task') {
      const td = e.data as unknown as { taskId: string };
      return !!tasksById[td.taskId];
    }
    return e.kind !== 'connector' && e.kind !== 'group';
  });

  // Hit-test entities against selection rect (skip locked layers)
  const rawMatches: string[] = [];
  for (const ent of entities) {
    if (lockedLayerIds.has(ent.layerId)) continue;
    if (ent.width > 0 && ent.height > 0) {
      if (rectsIntersect(rect, { x: ent.x, y: ent.y, width: ent.width, height: ent.height })) {
        rawMatches.push(ent.id);
      }
    } else {
      if (pointInRect(ent.x, ent.y, rect)) {
        rawMatches.push(ent.id);
      }
    }
  }

  // Build redirect maps
  const parentMap = new Map<string, string>();
  for (const g of groups) {
    for (const mid of g.memberEntityIds) parentMap.set(mid, g.id);
  }
  const groupMap = new Map<string, CanvasGroup>();
  for (const g of groups) groupMap.set(g.id, g);

  // Redirect entities inside invisible groups to outermost invisible ancestor
  const matchingSet = new Set<string>();
  for (const eid of rawMatches) {
    const redirectId = resolveGroupRedirect(eid, enteredGroupId, parentMap, groupMap);
    matchingSet.add(redirectId ?? eid);
  }

  // Compute group bounds
  const memberRectMap = new Map<string, { x: number; y: number; width: number; height: number }>();
  for (const e of entities) {
    memberRectMap.set(e.id, { x: e.x, y: e.y, width: e.width, height: e.height });
  }
  const layerHiddenNodeIds = new Set<string>();
  if (hiddenLayerIds.size > 0) {
    for (const e of entities) {
      if (hiddenLayerIds.has(e.layerId)) layerHiddenNodeIds.add(e.id);
    }
  }
  const groupBoundsMap = buildGroupBoundsMap(groups, memberRectMap, layerHiddenNodeIds);

  // Add container-mode groups that intersect (invisible groups are selected via member redirect)
  for (const [groupId, bounds] of groupBoundsMap) {
    if (groupId === enteredGroupId) continue;
    const g = groupMap.get(groupId);
    // Skip invisible groups from direct bounds intersection
    if (g && g.displayMode === 'invisible') continue;
    // Skip groups on locked layers
    if (g && lockedLayerIds.has(g.layerId)) continue;
    if (rectsIntersect(rect, bounds)) {
      matchingSet.add(groupId);
    }
  }

  return [...matchingSet];
}
